// ---------------------------------------------------------------------------
// saude-context-text.ts
//
// Converts already-loaded frontend page data into compact text for the AI chat
// context. The output format matches what the backend SaudeContextBuilder
// produces so the LLM system prompt can interpret it consistently.
//
// These functions run entirely on already-fetched data -- no network calls.
// ---------------------------------------------------------------------------

import { compararEquipesPiorPrimeiro, isEscalaInvertida, C1_FAIXA_IDEAL } from './saude-classificacao';

// -- helpers ----------------------------------------------------------------

function fmtCurrency(value: number): string {
  if (value == null || isNaN(value)) return '0,00';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtPct(value: number | undefined | null): string {
  if (value == null || isNaN(value)) return '0.0';
  return value.toFixed(1);
}

function fmtNum(value: number | undefined | null): string {
  if (value == null || isNaN(value)) return '0';
  return value.toLocaleString('pt-BR');
}

const ESF_EAP_EIXOS = [
  'c1-mais-acesso',
  'c2-criancas',
  'c3-gestantes',
  'c4-diabeticos',
  'c5-hipertensos',
  'c6-idosos',
  'c7-mulheres',
];

// ---------------------------------------------------------------------------
// 1. Dashboard (resumo geral + optional evolucao + optional completude)
// ---------------------------------------------------------------------------

export function buildDashboardContext(
  resumo: any,
  evolucao?: any,
  completude?: any,
): string {
  if (!resumo) return '';

  let text = '\n== RESUMO GERAL DO MUNICIPIO ==\n';
  text += `Score Municipal: ${resumo.score_geral ?? 'N/A'} (${resumo.classificacao_geral ?? 'N/A'})\n`;

  const eixos: any[] = resumo.eixos || [];

  // Group by category
  const esfEap = eixos.filter((e: any) => ESF_EAP_EIXOS.includes(e.eixo));
  const esb = eixos.filter((e: any) => e.eixo === 'odonto');
  const emulti = eixos.filter((e: any) => e.eixo === 'emulti');
  const comp = eixos.filter((e: any) =>
    ['producao', 'nt30-vinculo'].includes(e.eixo),
  );

  if (esfEap.length > 0) {
    const avg = Math.round(
      esfEap.reduce((s: number, e: any) => s + (e.score_medio || 0), 0) /
        esfEap.length,
    );
    text += `\neSF/eAP (media): ${avg}\n`;
    for (const e of esfEap) {
      text += `  ${e.nome}: ${e.score_medio} (${e.classificacao})`;
      if (e.total_identificados != null) {
        text += ` - ${fmtNum(e.total_identificados)} identificados`;
      }
      text += '\n';
    }
  }

  if (esb.length > 0) {
    text += `\neSB (Saude Bucal): ${esb[0].score_medio} (${esb[0].classificacao})\n`;
  }
  if (emulti.length > 0) {
    text += `eMulti: ${emulti[0].score_medio} (${emulti[0].classificacao})\n`;
  }
  if (comp.length > 0) {
    text += '\nComplementares:\n';
    for (const e of comp) {
      text += `  ${e.nome}: ${e.score_medio} (${e.classificacao})\n`;
    }
  }

  // Evolucao temporal (optional)
  if (evolucao?.periodos?.length) {
    text += '\n== EVOLUCAO TEMPORAL ==\n';
    for (const p of evolucao.periodos) {
      text += `  ${p.periodo}: ${p.score}\n`;
    }
    if (evolucao.tendencias) {
      const trends = Object.entries(evolucao.tendencias) as [string, any][];
      const notStable = trends.filter(([, v]) => v.trend !== 'stable');
      if (notStable.length > 0) {
        text += 'Tendencias:\n';
        for (const [eixo, v] of notStable) {
          const arrow = v.trend === 'up' ? '+' : '-';
          text += `  ${eixo}: ${arrow}${Math.abs(v.delta).toFixed(1)}\n`;
        }
      }
    }
  }

  // Completude / alertas (optional)
  if (completude?.alertas?.length) {
    text += '\n== ALERTAS DE COMPLETUDE ==\n';
    const maxAlertas = 5;
    for (const alerta of completude.alertas.slice(0, maxAlertas)) {
      text += `- ${alerta.mensagem}\n`;
    }
    if (completude.alertas.length > maxAlertas) {
      text += `(+ ${completude.alertas.length - maxAlertas} alertas adicionais)\n`;
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// 1b. Analysis context (enriched for AI predictive analysis)
// ---------------------------------------------------------------------------

export function buildAnalysisContext(
  resumo: any,
  evolucao?: any,
  completude?: any,
): string {
  if (!resumo) return '';

  // Start with the standard dashboard context
  let text = buildDashboardContext(resumo, evolucao, completude);

  const eixos: any[] = resumo.eixos || [];

  // Add indicator-level detail for eixos scoring below 50
  const lowEixos = eixos
    .filter((e: any) => (e.score_medio ?? 0) < 50 && e.indicadores?.length)
    .sort((a: any, b: any) => (a.score_medio ?? 0) - (b.score_medio ?? 0))
    .slice(0, 5);

  if (lowEixos.length > 0) {
    text += '\n== DETALHAMENTO DOS EIXOS CRITICOS ==\n';
    for (const e of lowEixos) {
      text += `\n${e.nome} (score ${e.score_medio}):\n`;
      for (const ind of e.indicadores) {
        const potencial = ((100 - (ind.percentual ?? 0)) * (ind.peso ?? 0)) / 100;
        text += `  ${ind.indicador}: ${fmtPct(ind.percentual)}% (${ind.numerador}/${ind.denominador}) peso=${ind.peso} potencial=+${potencial.toFixed(1)}pts\n`;
      }
    }
  }

  // Add top opportunities by potential score gain
  const allOpps: { eixo: string; ind: string; pct: number; potencial: number }[] = [];
  for (const e of eixos) {
    for (const ind of (e.indicadores || [])) {
      const potencial = ((100 - (ind.percentual ?? 0)) * (ind.peso ?? 0)) / 100;
      if (potencial > 1) {
        allOpps.push({ eixo: e.nome, ind: ind.indicador, pct: ind.percentual ?? 0, potencial });
      }
    }
  }
  allOpps.sort((a, b) => b.potencial - a.potencial);
  if (allOpps.length > 0) {
    text += '\n== TOP OPORTUNIDADES POR POTENCIAL ==\n';
    for (const o of allOpps.slice(0, 8)) {
      text += `  ${o.eixo} ${o.ind}: ${o.pct.toFixed(0)}% (potencial +${o.potencial.toFixed(1)} pts)\n`;
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// 2. Eixo detail (resumo + equipes por equipe)
// ---------------------------------------------------------------------------

export function buildEixoContext(resumo: any, equipes: any[]): string {
  if (!resumo) return '';

  let text = `\n== DETALHE: ${resumo.nome || 'Eixo'} ==\n`;
  text += `Score: ${resumo.score_medio} (${resumo.classificacao})\n`;
  if (isEscalaInvertida(resumo.eixo)) {
    text += `Escala deste eixo (nota metodologica, U invertido): Otimo >${C1_FAIXA_IDEAL.min}-${C1_FAIXA_IDEAL.max}%, `;
    text += `Bom >30-50%, Suficiente >10-30%, Regular <=10% ou >${C1_FAIXA_IDEAL.max}% `;
    text += '- percentual maior NAO e melhor.\n';
  }
  text += `Tipo: ${resumo.tipo || 'N/A'}\n`;
  text += `Populacao alvo: ${resumo.denominador != null ? fmtNum(resumo.denominador) : (resumo.total_identificados != null ? fmtNum(resumo.total_identificados) : 'N/A')}\n`;

  // Indicadores
  if (resumo.indicadores?.length) {
    text += '\nIndicadores:\n';
    for (const ind of resumo.indicadores) {
      text += `  ${ind.indicador} - ${ind.nome}: ${fmtPct(ind.percentual)}%`;
      text += ` (${ind.numerador}/${ind.denominador}) peso=${ind.peso}\n`;
    }
  }

  // Equipes: top 3 and bottom 3 (pior primeiro; no C1 a escala e invertida)
  if (equipes && equipes.length > 0) {
    const sorted = [...equipes].sort(compararEquipesPiorPrimeiro(resumo.eixo));
    const total = sorted.length;

    const worst = sorted.slice(0, Math.min(3, total));
    const best = sorted.slice(Math.max(0, total - 3)).reverse();

    text += `\nEquipes (${total} total):\n`;
    text += 'Piores:\n';
    for (const eq of worst) {
      text += `  ${eq.equipe} (${eq.ine}): ${eq.score} (${eq.classificacao})`;
      if (eq.unidade) text += ` [${eq.unidade}]`;
      text += '\n';
      // Show per-indicator breakdown for worst teams
      if (eq.indicadores?.length) {
        for (const ind of eq.indicadores) {
          text += `    ${ind.indicador}: ${fmtPct(ind.percentual)}% (${ind.numerador}/${ind.denominador})\n`;
        }
      }
    }
    text += 'Melhores:\n';
    for (const eq of best) {
      text += `  ${eq.equipe} (${eq.ine}): ${eq.score} (${eq.classificacao})`;
      if (eq.unidade) text += ` [${eq.unidade}]`;
      text += '\n';
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// 3. Financeiro (simulacao financeira)
// ---------------------------------------------------------------------------

export function buildFinanceiroContext(financeiro: any): string {
  if (!financeiro?.resumo) return '';

  const r = financeiro.resumo;
  let text = '\n== SIMULACAO FINANCEIRA ==\n';
  text += `Receita mensal atual: R$ ${fmtCurrency(r.receitaMensal)}\n`;
  text += `Receita mensal maxima: R$ ${fmtCurrency(r.receitaMaxima)}\n`;
  text += `Gap mensal: R$ ${fmtCurrency(r.gap)}\n`;
  text += `Gap anual: R$ ${fmtCurrency(r.gapAnual)}\n`;

  if (financeiro.classificacoes) {
    const entries = Object.entries(financeiro.classificacoes);
    text += 'Classificacoes: ';
    text += entries.map(([k, v]) => `${k}=${v}`).join(', ') + '\n';
  }

  // Equipes with largest gap (bottom 3 by valor gap)
  if (financeiro.equipes?.length) {
    const sorted = [...financeiro.equipes].sort(
      (a: any, b: any) =>
        (b.valorMaximo - b.valorAtual) - (a.valorMaximo - a.valorAtual),
    );
    const total = sorted.length;
    const topGap = sorted.slice(0, Math.min(3, total));

    text += `\nEquipes com maior gap (de ${total}):\n`;
    for (const eq of topGap) {
      const gap = eq.valorMaximo - eq.valorAtual;
      text += `  ${eq.equipe} (${eq.ine}): ${eq.classificacao}`;
      text += ` - atual R$ ${fmtCurrency(eq.valorAtual)}`;
      text += ` / max R$ ${fmtCurrency(eq.valorMaximo)}`;
      text += ` (gap R$ ${fmtCurrency(gap)})\n`;
    }

    // Also show best performing (smallest gap)
    const bestPerf = sorted.slice(Math.max(0, total - 3)).reverse();
    if (bestPerf.length > 0 && bestPerf[0] !== topGap[0]) {
      text += 'Equipes com menor gap:\n';
      for (const eq of bestPerf) {
        const gap = eq.valorMaximo - eq.valorAtual;
        text += `  ${eq.equipe} (${eq.ine}): ${eq.classificacao}`;
        text += ` - gap R$ ${fmtCurrency(gap)}\n`;
      }
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// 3b. Resumo Executivo do Quadrimestre (briefing do gestor)
// ---------------------------------------------------------------------------

export function buildResumoExecutivoContext(resumo: any): string {
  if (!resumo) return '';

  const fin = resumo.financeiro || {};
  let text = '\n== RESUMO EXECUTIVO DO QUADRIMESTRE ==\n';
  text += `Score consolidado: ${resumo.score_geral ?? 'N/A'} (${resumo.classificacao_geral ?? 'N/A'})\n`;

  text += '\nFinanciamento (cofinanciamento federal APS):\n';
  text += `  Receita assegurada (atual): R$ ${fmtCurrency(fin.receitaMensal)}/mes (R$ ${fmtCurrency(fin.receitaAnual)}/ano)\n`;
  text += `  Receita potencial (Otimo): R$ ${fmtCurrency(fin.receitaMaxima)}/mes (R$ ${fmtCurrency(fin.receitaMaximaAnual)}/ano)\n`;
  text += `  Em jogo (gap): R$ ${fmtCurrency(fin.gap)}/mes (R$ ${fmtCurrency(fin.gapAnual)}/ano)\n`;
  if (resumo.gap_indicadores != null) {
    text += `  Recuperavel via indicadores (Qualidade+Vinculo): R$ ${fmtCurrency(resumo.gap_indicadores)}/mes\n`;
  }

  if (resumo.top_unidades_risco?.length) {
    text += '\nTop unidades em risco (eSF/eAP, R$/mes em jogo estimado):\n';
    for (const u of resumo.top_unidades_risco) {
      text += `  ${u.nome}: R$ ${fmtCurrency(u.r_estimado)}/mes estimado - ${u.equipes} equipe(s), score ${u.score_medio} (${u.classificacao})\n`;
    }
  }

  if (resumo.top_indicadores_risco?.length) {
    text += '\nIndicadores que mais impactam o financiamento (R$ estimado/mes):\n';
    for (const ind of resumo.top_indicadores_risco) {
      text += `  ${ind.nome} (${ind.classificacao}, score ${ind.score_medio}): R$ ${fmtCurrency(ind.r_estimado)}/mes\n`;
    }
  }

  // Eixos criticos (score < 50) para embasar priorizacao
  const criticos = (resumo.eixos || [])
    .filter((e: any) => (e.score_medio ?? 0) < 50)
    .sort((a: any, b: any) => (a.score_medio ?? 0) - (b.score_medio ?? 0))
    .slice(0, 6);
  if (criticos.length) {
    text += '\nEixos criticos (score < 50):\n';
    for (const e of criticos) {
      text += `  ${e.nome}: ${e.score_medio} (${e.classificacao})\n`;
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// 3c. Ritmo de Visitas do ACS (por microarea)
// ---------------------------------------------------------------------------

export function buildRitmoAcsContext(ritmo: any): string {
  if (!ritmo) return '';

  let text = '\n== RITMO DE VISITAS DO ACS ==\n';
  text += `Pacientes a visitar (total): ${fmtNum(ritmo.total_pacientes_pendentes)}\n`;
  text += `Data de fechamento do quadrimestre: ${ritmo.data_fechamento || '-'}\n`;
  text += `Dias uteis restantes: ${fmtNum(ritmo.dias_uteis_restantes)}\n`;
  if (ritmo.total_visitas_por_dia != null) {
    text += `Ritmo sugerido (municipio/unidade): ~${fmtNum(ritmo.total_visitas_por_dia)} visita(s) por dia\n`;
  }

  const micros: any[] = ritmo.microareas || [];
  if (micros.length) {
    text += `\nMicroareas com mais pacientes a visitar (de ${micros.length}):\n`;
    for (const m of micros.slice(0, 10)) {
      const eq = m.equipe ? ` [Equipe ${m.equipe}]` : '';
      const ritmoMic = m.visitas_por_dia != null ? ` -> ~${m.visitas_por_dia}/dia` : '';
      text += `  Microarea ${m.micro}${eq}: ${fmtNum(m.gap_visitas)} a visitar${ritmoMic}\n`;
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// 4. Ranking (ranking de equipes)
// ---------------------------------------------------------------------------

export function buildRankingContext(ranking: any): string {
  if (!ranking?.equipes?.length) return '';

  const equipes = ranking.equipes;
  const total = equipes.length;
  let text = `\n== RANKING EQUIPES (${total} equipes) ==\n`;

  if (ranking.distribuicao) {
    text += `Distribuicao: Otimo=${ranking.distribuicao.Otimo || 0}`;
    text += ` Bom=${ranking.distribuicao.Bom || 0}`;
    text += ` Suficiente=${ranking.distribuicao.Suficiente || 0}`;
    text += ` Regular=${ranking.distribuicao.Regular || 0}\n`;
  }

  // Assume equipes are sorted ascending by score (like backend)
  const sorted = [...equipes].sort(
    (a: any, b: any) => (a.scoreConsolidado || 0) - (b.scoreConsolidado || 0),
  );

  // Bottom 3 (worst)
  const bottom = sorted.slice(0, Math.min(3, total));
  text += '\nPiores:\n';
  for (const eq of bottom) {
    text += `  ${eq.equipe} (${eq.ine}): ${eq.scoreConsolidado} (${eq.classificacao})`;
    if (eq.unidade) text += ` [${eq.unidade}]`;
    text += '\n';
    // Show per-eixo scores for worst teams
    if (eq.eixos) {
      const eixoEntries = Object.entries(eq.eixos) as [string, any][];
      const lowEixos = eixoEntries
        .filter(([, v]) => v.score != null)
        .sort(([, a], [, b]) => (a.score || 0) - (b.score || 0))
        .slice(0, 3);
      if (lowEixos.length > 0) {
        for (const [eixo, v] of lowEixos) {
          text += `    ${eixo}: ${v.score} (${v.classificacao})\n`;
        }
      }
    }
  }

  // Top 3 (best)
  const top = sorted.slice(Math.max(0, total - 3)).reverse();
  text += 'Melhores:\n';
  for (const eq of top) {
    text += `  ${eq.equipe} (${eq.ine}): ${eq.scoreConsolidado} (${eq.classificacao})`;
    if (eq.unidade) text += ` [${eq.unidade}]`;
    text += '\n';
  }

  return text;
}

// ---------------------------------------------------------------------------
// 5. Painel MS (structure can vary - use generic key-value dump)
// ---------------------------------------------------------------------------

export function buildPainelMSContext(painel: any): string {
  if (!painel) return '';

  let text = '\n== PAINEL MS ==\n';

  if (painel.financiamento) {
    text += '\nFinanciamento:\n';
    text += formatObjectCompact(painel.financiamento, '  ');
  }

  if (painel.suspensao) {
    text += '\nSuspensao:\n';
    text += formatObjectCompact(painel.suspensao, '  ');
  }

  if (painel.cobertura) {
    text += '\nCobertura:\n';
    text += formatObjectCompact(painel.cobertura, '  ');
  }

  // Catch any other top-level keys not already handled
  const knownKeys = new Set(['financiamento', 'suspensao', 'cobertura']);
  for (const [key, value] of Object.entries(painel)) {
    if (!knownKeys.has(key) && value != null) {
      text += `\n${key}:\n`;
      if (typeof value === 'object') {
        text += formatObjectCompact(value, '  ');
      } else {
        text += `  ${value}\n`;
      }
    }
  }

  return text;
}

// ---------------------------------------------------------------------------
// Generic object formatter (compact, max 2 levels deep)
// ---------------------------------------------------------------------------

function formatObjectCompact(obj: any, indent: string, depth = 0): string {
  if (obj == null) return '';
  if (typeof obj !== 'object') return `${indent}${obj}\n`;
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '';
    // For arrays of primitives, join inline
    if (typeof obj[0] !== 'object') {
      return `${indent}${obj.join(', ')}\n`;
    }
    // For arrays of objects, list first 5
    let text = '';
    const limit = Math.min(obj.length, 5);
    for (let i = 0; i < limit; i++) {
      text += formatObjectCompact(obj[i], indent + '  ', depth + 1);
      if (i < limit - 1) text += indent + '  ---\n';
    }
    if (obj.length > limit) {
      text += `${indent}  (+ ${obj.length - limit} itens)\n`;
    }
    return text;
  }

  // Plain object
  let text = '';
  const maxDepth = 2;
  for (const [key, value] of Object.entries(obj)) {
    if (value == null) continue;
    if (typeof value === 'object' && depth < maxDepth) {
      text += `${indent}${key}:\n`;
      text += formatObjectCompact(value, indent + '  ', depth + 1);
    } else if (typeof value === 'number') {
      // Format large numbers nicely
      if (Number.isInteger(value) && Math.abs(value) >= 1000) {
        text += `${indent}${key}: ${fmtNum(value)}\n`;
      } else if (!Number.isInteger(value)) {
        text += `${indent}${key}: ${typeof value === 'number' && key.toLowerCase().includes('valor') ? 'R$ ' + fmtCurrency(value) : value}\n`;
      } else {
        text += `${indent}${key}: ${value}\n`;
      }
    } else if (typeof value !== 'object') {
      text += `${indent}${key}: ${value}\n`;
    } else {
      // depth >= maxDepth, stringify compactly
      text += `${indent}${key}: ${JSON.stringify(value)}\n`;
    }
  }
  return text;
}
